/**
 * Partners — the NP step (stages `plan_built` + `np_selected`; the
 * former is invisible in the workspace, folded in here).
 *
 * Top: a compact scope block (the retired Plan screen compressed to
 * component groups + counts with expandable requirement rows). Below:
 * the existing assignment machinery — ScopeGrid + AssignmentCards —
 * mounted inside the section via NpAssignmentsProvider, with lock-in
 * mirrored on the cards. The gate strip owns Continue; a failed advance
 * surfaces its 409 detail here as the readiness / Backoffice alerts.
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Accordion, Anchor, Badge, Button, Card, Group, Loader, Stack, Text } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';

import {
  AssignmentCard,
  AssignmentCommercialsForm,
  CancelAssignmentControl,
  CancelledNote,
  ManualRegistryOrders,
  NpAssignmentAside,
  NpAssignmentsProvider,
  readConflictAlertProps,
  ReadinessConflictAlerts,
  ScopeGrid,
  useNpAssignmentsContext,
} from '@/components/NpAssignments';
import { NpMissingBackofficeBanner } from '@/components/NpMissingBackofficeBanner';
import { RegistryOrdersCard } from '@/components/RegistryOrders';
import { useLayoutOptional } from '@/contexts/LayoutContext';
import { useLegacyTemplateTiles } from '@/hooks/useLegacyTemplateTiles';
import { useNpMissingBackofficeBanner } from '@/hooks/useNpMissingBackofficeBanner';
import { useResearchPlan } from '@/hooks/useResearchPlan';
import { isAssignmentCancelled, isManagedAssignment } from '@/types/np-assignments';
import { registryDocumentsOrderable, registryDocumentsShown } from '@/types/registry-documents';
import type { ReportRequestDetail } from '@/types/report-request';

interface PartnersSectionProps {
  detail: ReportRequestDetail;
  /** 409 detail from the strip's failed advance(ready_to_start), if any. */
  conflictDetail: Record<string, unknown> | null;
  /** True while this section's stage is the active one — the NP chat
   * aside only registers then (other stages keep the default chat aside). */
  isActiveStage: boolean;
  /** True when the request is past Partners — the scope and partners
   * are mirrored into the Backoffice project and frozen; the record
   * keeps the commercials (fee / deadline) editable, synced on write. */
  readOnly?: boolean;
  /** Re-reads `detail` once a lock, unlock, cancel or delete has gone
   * through: its `registryDocuments` gate follows whether Armadillo is
   * locked, so the card above the assignments follows without a reload. */
  onLockChanged?: () => void | Promise<void>;
}

export function PartnersSection({
  detail,
  conflictDetail,
  isActiveStage,
  readOnly = false,
  onLockChanged,
}: Readonly<PartnersSectionProps>) {
  // Worker-bounced delegation (np_missing_in_backoffice / files) banner.
  const banner = useNpMissingBackofficeBanner(detail.id);

  // Legacy (Backoffice docx) reports group Django-template scope
  // components instead of HUMAN plan items.
  const legacy = useLegacyTemplateTiles(detail.id, detail.reportConfigId);

  return (
    <NpAssignmentsProvider
      reportId={detail.reportId}
      reportRequestId={detail.id}
      reportCompleted={detail.reportLifecycleStage === 'completed'}
      reportStarted={detail.triageStage === 'started'}
      source={legacy.isLegacy ? 'template_components' : 'research_plan'}
      template={legacy.template}
      templateSubjects={legacy.subjects}
      templateLoading={legacy.loading}
      onLockChanged={onLockChanged}
    >
      {isActiveStage ? (
        <NpAsideRegistrar detailId={detail.id} />
      ) : (
        <LateDraftAside detailId={detail.id} />
      )}
      <Stack gap="md">
        {!legacy.isLegacy && <PlanScopeBlock reportId={detail.reportId} />}
        {/* Registry documents (Armadillo) sit beside the partner work once
            Armadillo i-doc is locked on one of the assignments below: its
            corporate-records line is then filled by orders, prepared here
            and sent at Start. Until then (another partner may cover that
            line) the card shows only the orders the report already has,
            without the way to order more, and follows the lock through
            `onLockChanged`. */}
        {detail.reportId && registryDocumentsShown(detail.registryDocuments) && (
          <RegistryOrdersCard
            reportId={detail.reportId}
            subjectSource={{ reportRequestId: detail.id }}
            reportStage={detail.reportLifecycleStage}
            canOrder={registryDocumentsOrderable(detail.registryDocuments)}
          />
        )}
        <NpMissingBackofficeBanner item={banner.item} />
        {readOnly ? <PartnersRecord /> : <PartnersBody conflictDetail={conflictDetail} />}
      </Stack>
    </NpAssignmentsProvider>
  );
}

/** A partner added after Start is a draft assignment that needs the shortlist
 * and chat like any other, so the aside is registered while one exists. */
function LateDraftAside({ detailId }: Readonly<{ detailId: string }>) {
  const { assignments, reportStarted } = useNpAssignmentsContext();
  // Armadillo's automatic assignment is no draft: nothing is shortlisted or
  // agreed over chat for it.
  const hasDraft =
    reportStarted &&
    assignments.some((a) => a.status !== 'sent_to_backoffice' && !isManagedAssignment(a));
  return hasDraft ? <NpAsideRegistrar detailId={detailId} /> : null;
}

/** Registers the NP shortlist/chat aside while the np_selected stage is
 * mounted; rendered inside the provider so the aside shares its store. */
function NpAsideRegistrar({ detailId }: Readonly<{ detailId: string }>) {
  const registerAside = useLayoutOptional()?.registerAside;
  useEffect(() => {
    if (!registerAside) {
      return;
    }
    registerAside({
      title: `Triage · ${detailId.slice(0, 8)}…`,
      content: <NpAssignmentAside />,
    });
    return () => {
      registerAside(null);
    };
  }, [registerAside, detailId]);
  return null;
}

/**
 * Record of the assignments once the request is past Partners: the scope
 * and partner are frozen (the Backoffice project mirrors them — "Change"
 * on the section is the way back in), but the fee and deadline stay
 * editable even after the work has started, syncing to the Backoffice
 * partner request on every write.
 */
export function PartnersRecord() {
  const { assignments, isLoading, reportStarted, reportCompleted, actions } =
    useNpAssignmentsContext();
  // Another partner can be added while the report is under way.
  const canAddPartner = reportStarted && !reportCompleted;

  if (isLoading && assignments.length === 0) {
    return (
      <Group>
        <Loader size="sm" />
        <Text size="sm">Loading assignments…</Text>
      </Group>
    );
  }

  if (assignments.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No Network Partner assignments on this request.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Scope and partners are locked past this step — use "Change" on this section to revert and
        edit them. Fees and deadlines stay editable and sync to Backoffice.
        {canAddPartner &&
          ' To have a second partner cover the same scope, use "Add another partner".'}
      </Text>
      {assignments.map((a, idx) => {
        // A partner added after Start is still a draft: it gets the full card
        // (shortlist, lock-in, fee, deadline, files) and a dispatch button.
        // Armadillo's automatic assignment gets its read-only card, sent or not.
        if (a.status !== 'sent_to_backoffice' || isManagedAssignment(a)) {
          return <AssignmentCard key={a.id} assignment={a} index={idx} />;
        }
        const np = a.networkPartner?.name ?? a.networkPartner?.email ?? 'no partner';
        const cancelled = isAssignmentCancelled(a);
        return (
          <Card
            key={a.id}
            withBorder
            padding="sm"
            radius="md"
            data-testid={`partners-record-${a.id}`}
            style={{ opacity: cancelled ? 0.75 : undefined }}
          >
            <Stack gap="xs">
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color={cancelled ? 'gray' : a.isReady ? 'green' : 'yellow'}>
                  A{idx + 1}
                </Badge>
                {cancelled && (
                  <Badge variant="light" color="gray">
                    Cancelled
                  </Badge>
                )}
                {a.networkPartnerId ? (
                  <Anchor
                    component={Link}
                    to={`/admin/network-partners/${a.networkPartnerId}`}
                    size="sm"
                    data-testid={`partner-manage-${a.networkPartnerId}`}
                  >
                    {np}
                  </Anchor>
                ) : (
                  <Text size="sm">{np}</Text>
                )}
                <Text size="xs" c="dimmed">
                  · {a.items.length} item(s)
                </Text>
              </Group>
              <CancelledNote assignment={a} />
              <AssignmentCommercialsForm
                assignment={a}
                dispatched={a.status === 'sent_to_backoffice'}
              />
              <ManualRegistryOrders assignment={a} />
              <Group gap="xs">
                <CancelAssignmentControl assignment={a} />
                {canAddPartner && (
                  <Button
                    size="compact-xs"
                    variant="light"
                    leftSection={<IconUserPlus size={12} />}
                    onClick={() => void actions.duplicate(a.id)}
                    data-testid={`add-partner-${a.id}`}
                  >
                    Add another partner
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

function PartnersBody({
  conflictDetail,
}: Readonly<{ conflictDetail: Record<string, unknown> | null }>) {
  const { assignments, humanItems, unassignedHumanItemIds, ready, isLoading } =
    useNpAssignmentsContext();

  if (isLoading && assignments.length === 0 && humanItems.length === 0) {
    return (
      <Group>
        <Loader size="sm" />
        <Text size="sm">Loading assignments…</Text>
      </Group>
    );
  }

  if (humanItems.length === 0 && assignments.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This report has no items that need a Network Partner — continue from the strip above.
      </Text>
    );
  }

  const conflict = conflictDetail
    ? readConflictAlertProps(conflictDetail)
    : { missingIds: [], backofficeMissing: null };
  const allGrouped = unassignedHumanItemIds.length === 0;
  const canAdvance = allGrouped && ready;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Group items into assignments, agree a fee and turnaround with a Network Partner over chat,
          then lock one in per assignment.
        </Text>
        <Badge color={canAdvance ? 'green' : 'yellow'} variant="light">
          {assignments.filter((a) => a.isReady).length} / {assignments.length} ready
        </Badge>
      </Group>

      <ScopeGrid />

      <ReadinessConflictAlerts
        missingIds={conflict.missingIds}
        backofficeMissing={conflict.backofficeMissing}
      />

      {assignments.length > 0 ? (
        <Stack gap="md">
          {assignments.map((a, idx) => (
            <AssignmentCard key={a.id} assignment={a} index={idx} />
          ))}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          No assignments yet — select items in the scope above and click “Create assignment”.
        </Text>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Compact scope block — the retired Plan screen as a summary row + accordion
// ---------------------------------------------------------------------------

function PlanScopeBlock({ reportId }: Readonly<{ reportId: string | null }>) {
  const { data, isLoading } = useResearchPlan(reportId);

  if (isLoading || !data || data.groups.length === 0) {
    return null;
  }

  const totalItems = data.groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <Card withBorder padding="sm" radius="md" data-testid="plan-scope-block">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Research scope
          </Text>
          <Badge variant="light" color="blue" size="sm">
            {totalItems} item(s) · {data.groups.length} component(s)
          </Badge>
        </Group>
        <Accordion variant="contained" radius="sm" multiple>
          {data.groups.map((group) => (
            <Accordion.Item key={group.scope_component_id} value={group.scope_component_id}>
              <Accordion.Control>
                <Group gap="xs">
                  <Text size="sm">{group.scope_component_name}</Text>
                  <Badge size="xs" variant="default">
                    {group.items.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap={4}>
                  {group.items.map((item) => (
                    <Group key={item.id} gap="xs" wrap="wrap">
                      <Badge
                        size="xs"
                        variant="light"
                        color={item.item_type === 'human' ? 'orange' : 'blue'}
                      >
                        {item.item_type.toUpperCase()}
                      </Badge>
                      <Text size="xs">{item.scope_component_slug}</Text>
                      {item.subject_name && (
                        <Text size="xs" c="dimmed">
                          · {item.subject_name}
                        </Text>
                      )}
                      {item.dimensions.jurisdiction && (
                        <Text size="xs" c="dimmed">
                          · {item.jurisdiction_name ?? item.dimensions.jurisdiction}
                        </Text>
                      )}
                    </Group>
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </Card>
  );
}
